import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { PopupSummaryGeneratorService } from './worker/popup-summary-generator.service';
import { Project } from '../../models/project/project.model';
import { Results } from '../../models/results/results.model';
import { Unit, UnitService } from '../anessa-library/anessa-library/projects/unit-library/src/public-api';
import { ConfiguredToastrService } from '../toastr/configured-toastr.service';
import { CommunicationService } from '../../communication/communication.service';
import { takeUntil, takeWhile } from 'rxjs/operators';
import { Product, ProductType } from '../../models/project/plant-specifications/product.model';
import { Sprite, Sprites } from '../../models/data-structures/sprites.model';
import { Feedstock } from '../../models/project/feedstock/feedstock.model';
import Utils from '../../util';
import { DigesterType, PlantUnitType } from '../../models/project/plant-specifications/plant-specifications.model';
import { FeedstockService } from '../../project/feedstock/feedstock.service';
import { ResultRow } from '../../models/data-structures/results/result-row.model';
import { AnessaTranslationService } from '../translation/anessa-translation.service';
import { AnessaTranslationInterface } from '../../models/interfaces/anessa-translation.interface';
import { DropdownDisplayItem } from '../../models/data-structures/dropdown-display-item';
import { BehaviorSubject } from 'rxjs';
import { ResultSetConfig } from '../../models/results/result-set-config.model';
import {
  ResultSetDropdownId
} from '../../models/enums/results/result-set-dropdown-ids/result-set-dropdown-config-id.enum';
import { UserpilotReloader } from '../userpilot-reloader.component';
import FormatUtil from '../format.util';

import cytoscapePopper from 'cytoscape-popper';
import cytoscape from 'cytoscape';
import { createPopper, Instance as PopperInstance } from '@popperjs/core';
import { LooseObject } from '../../models/loose-object.model';
import { TechnicalResults } from '../../models/results/technical/technical-results.model';
import { PlantProductDemand } from '../../models/results/technical/ppd/ppd.model';

cytoscape.use(cytoscapePopper(createPopper));

@Component({
  selector: 'app-flow-diagram-creator',
  templateUrl: './flow-diagram-creator.component.html',
  styleUrls: ['./flow-diagram-creator.component.scss'],
  providers: [FeedstockService]
})
export class FlowDiagramCreatorComponent extends UserpilotReloader implements OnInit, OnDestroy, AnessaTranslationInterface {
  private cy: cytoscape.Core = cytoscape();
  private onDestroyNotifier = new EventEmitter();
  private currentColumn = 0;

  graphReady = false;
  trigger?: string;
  private popperDivCurrentNode: any;

  constructor(
    private router: Router,
    private summaryGenerator: PopupSummaryGeneratorService,
    private unitService: UnitService,
    private feedstockService: FeedstockService,
    private toastrService: ConfiguredToastrService,
    private commService: CommunicationService,
    private translationService: AnessaTranslationService
  ) {
    super();
  }

  @Input() project?: Project;
  @Input() results?: Results;
  @Input() precision = 2;
  @Input() updateGraphSignal?: BehaviorSubject<{ project: Project, results: Results }>;
  @Input() resultSetConf?: ResultSetConfig;
  @Input() resultSetConfUpdatedNotifier?: EventEmitter<ResultSetConfig>;
  @Output() resultSetConfSaveNotifier: EventEmitter<ResultSetConfig> = new EventEmitter();
  addResults = false;
  result_keys: string[] = [];
  processingNodes: ProcessNode[] = [];
  products: Product[] = [];
  spacingFactor = 1.00;
  private recyclingAdded = false;
  currentPopperPopup?: PopperInstance;
  modelIdMap: LooseObject = {};
  unitServiceLoaded = false;
  @ViewChild('popperDiv') private popperDiv?: ElementRef<HTMLElement>;

  /** Result period options */
  periodCategories: DropdownDisplayItem[] = [];
  /** Default result period */
  @Input() periodType = 'year';
  /** Measurement system options */
  techUnitTypes: DropdownDisplayItem[] = [];
  /** Default measurement system */
  techUnitType = 'metric';

  @Input() hideMeasurementSelect = false;
  @Input() hideSpacingSlider = false;
  @Input() hideSaveButtons = false;

  warnings: LooseObject = {};

  updatePopper = () => {
    if (this.currentPopperPopup) {
      this.currentPopperPopup.update();
    }
  }
  getSliderLabelFromIndex = (value: number) => {
    return this.result_keys[value];
  }

  ngOnInit(): void {
    this.setTranslations();
    window.addEventListener('scroll', this.updatePopper, true);
    this.unitService.unitsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .pipe(takeWhile(x => !x, true))
      .subscribe((loaded: boolean) => {
        this.unitServiceLoaded = loaded;
        this.setup();
      });
    if (this.updateGraphSignal) {
      this.updateGraphSignal.pipe(takeUntil(this.onDestroyNotifier)).subscribe(
        (data) => {
          if (data && data.project && data.results) {
            this.project = data.project;
            this.results = data.results;
            setTimeout(() => {
              this.setup();
            });
          }
        }
      );
    }
    if (this.resultSetConfUpdatedNotifier) {
      this.resultSetConfUpdatedNotifier
        .pipe(takeUntil(this.onDestroyNotifier))
        .subscribe((updatedConf: ResultSetConfig) => {
          this.resultSetConf = updatedConf; // to solve a timing issue
          this.processResultSetDefaults();
          this.setup();
        });
    }
    if (!this.unitService.unitsLoadedNotifier.value) {
      this.unitService.loadData();
    }
    if (this.project) {
      if (this.project.projectInfo.defaults.unitSystem) this.techUnitType = this.project.projectInfo.defaults.unitSystem;
      if (this.project.plantSpecifications && this.project.plantSpecifications.generatedProducts) {
        this.products = Utils.deepCopy(this.project.plantSpecifications.generatedProducts);
      }
      if (this.project.projectInfo && this.project.projectInfo.defaults) {
        const defaults = this.project.projectInfo.defaults;
        if (defaults.decimalPrecision && defaults.decimalPrecision) {
          this.precision = defaults.decimalPrecision.value;
        }
      }
    }
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        this.setup();
        if (this.popperDivCurrentNode) {
          this.createPopperWindowForNode(this.popperDivCurrentNode);
        }
        this.trigger = language;
      });
  }

  private setTranslations() {
    this.periodCategories = [
      new DropdownDisplayItem('RESULTS.GENERAL.DAY', 'day'),
      new DropdownDisplayItem('RESULTS.GENERAL.MONTH', 'month'),
      new DropdownDisplayItem('RESULTS.GENERAL.YEAR', 'year')
    ];
    this.techUnitTypes = [
      new DropdownDisplayItem('RESULTS.GENERAL.METRIC', 'metric'),
      new DropdownDisplayItem('RESULTS.GENERAL.IMPERIAL', 'imperial')
    ];
  }

  public setup() {
    if (this.unitServiceLoaded) {
      this.addResults = !!this.results && !!this.results.technical;
      this.collectProcessingNodes();
      this.initGraph();
    }
  }

  onDropdownChange() {
    this.setup();
    if (this.resultSetConf?.dropdownConfigEnabled) {
      this.resultSetConf.dropdownConfigEnabled = false;
      this.resultSetConfSaveNotifier.emit(this.resultSetConf);
    }
  }


  /**
   * This function controls the flow
   */
  initGraph() {
    this.graphReady = false;
    let proceed = true;
    this.createCyAndSyles(); // good
    this.currentColumn = 0;
    if (!proceed) return;
    proceed = this.addFeedstocks();
    this.currentColumn++;

    if (!proceed || !this.project) return;
    // Add fresh water and mixer if wet continuous
    if (this.project.plantSpecifications.selectedDigester !== DigesterType.DRY_BATCH) {
      proceed = this.addMixerAndFreshWater();
      this.currentColumn++;
    }
    if (!proceed) return;
    proceed = this.addDigesters();
    this.currentColumn++;

    if (!proceed) return;
    proceed = this.addProcessing();
    this.currentColumn++;

    if (!proceed) return;
    proceed = this.addProducts();
    if (!proceed) return;
    proceed = this.connectNodes();

    if (!proceed) return;
    this.addClickListeners();
    this.setNodePositions();
    this.setGraphLayoutAndOptions();
    this.graphReady = true;
  }

  private setGraphLayoutAndOptions() {
    const options = {
      name: 'grid',
      spacingFactor: this.spacingFactor,
      condense: false,
      avoidOverlap: true,
      padding: 10,
      position: (node: any) => {
        return {
          row: node.data('row'),
          col: node.data('col')
        };
      }
    };
    this.cy.layout(options).run();
  }

  private addClickListeners() {
    // add click listeners
    this.cy.on('click', 'node', (evt) => {
      if (evt.target.data('navigateTo') !== undefined) {
        this.router.navigateByUrl(evt.target.data('navigateTo'));
      }
    });
    // on right click
    this.cy.on('cxttap', 'node', (evt) => {
      if (!this.popperDiv) return;
      this.popperDiv.nativeElement.style.visibility = 'visible';
      this.popperDivCurrentNode = evt.target.data('id');
      this.createPopperWindowForNode(this.popperDivCurrentNode);
    });
    this.cy.on('click', () => {
      if (!this.popperDiv) return;
      this.popperDiv.nativeElement.style.visibility = 'hidden';
      this.popperDiv.nativeElement.innerHTML = '';
    });
    // add drag event listener
    this.cy.on('drag', 'node', (evt) => {
      if (evt.target.data('helperNodes') !== undefined) {
        // Move any helper nodes with the parent node
        for (const nodeId of evt.target.data('helperNodes')) {
          const node = this.cy.getElementById(nodeId);
          node.position('x', evt.target.position('x'));
          node.position('y', evt.target.position('y'));
        }
      }
    });
  }

  private setNodePositions() {
    const rowCounts: number[] = [];
    // Get boundaries
    this.cy.nodes().forEach((node) => {
      if (rowCounts[node.data('col')] === undefined) {
        rowCounts[node.data('col')] = 0;
      }

      // Ignore invisible helper nodes
      if (node.data('label') !== '') {
        rowCounts[node.data('col')] += 1;
      }
    });
    const rows = Math.max(...rowCounts);
    const centerRow = Math.floor(rows / 2);
    // Update row positions
    this.cy.nodes().forEach((node: any) => {
      // Ignore rows that are already set
      if (node.data('row') !== undefined && Number.isInteger(node.data('row'))) {
        return;
      }
      if (node.data('row') === 'center' || rowCounts[node.data('col')] === 1) {
        node.data('row', centerRow);
        return;
      }
    });
  }

  private addMixerAndFreshWater() {
    if (!this.project) return false;
    const maxHeight = Math.max(this.project.feedstocks.length, this.products.length);
    if (!this.checkElements(['cy'])) {
      return false;
    }
    this.cy.add([
      {
        data: {
          id: 'waterIn',
          label: FormatUtil.supToUnicode(this.translationService.get('PFD.FRESH_WATER') + ' \n' + this.getFreshWaterInput()),
          type: 'rectangle',
          col: this.currentColumn,
          row: Math.max(Math.floor(maxHeight / 2) - 3, 0),
          height: Sprites.FRESH_WATER.height,
          width: Sprites.FRESH_WATER.width,
          image: Sprites.FRESH_WATER.image
        }
      },
      {
        data: {
          id: 'mixer',
          label: FormatUtil.supToUnicode(this.translationService.get('PFD.MIXER')),
          type: 'rectangle',
          col: this.currentColumn,
          row: 'center',
          height: Sprites.MIXER.height,
          width: Sprites.MIXER.width,
          image: Sprites.MIXER.image
        }
      },
      {
        data: {
          id: 'waterToMixer',
          type: 'rectangle',
          label: '',
          source: 'waterIn',
          target: 'mixer'
        }
      }
    ]);
    this.currentColumn++;
    if (!this.checkElements(['cy'])) {
      return false;
    }
    this.cy.add([
      {
        data: {
          id: 'influent',
          type: 'rectangle',
          label: FormatUtil.supToUnicode(this.translationService.get('PFD.INFLUENT') + '\n' + this.getInfluent()),
          col: this.currentColumn,
          row: 'center',
          width: Sprites.INFLUENT_FLOW.width,
          height: Sprites.INFLUENT_FLOW.height,
          image: Sprites.INFLUENT_FLOW.image
        }
      },
      {
        data: {
          id: 'mixer_influent',
          label: '',
          source: 'mixer',
          target: 'influent',
        }
      }
    ]);
    return true;
  }

  private collectProcessingNodes() {
    if (!this.project) return;
    this.processingNodes = [];

    const units = this.project.plantSpecifications.selectedUnits;

    for (const unit of units) {
      const unitType = this.getUnitTypeFromString(unit);
      if (!unitType) continue;
      this.processingNodes.push(new ProcessNode(unitType, '/project/plant-specifications'));
    }
  }

  private createCyAndSyles() {
    const style: cytoscape.CssStyleDeclaration = [
      {
        // node styling
        'selector': 'node',
        style: {
          label: 'data(label)',
          shape: 'rectangle',
          width: 'data(width)',
          height: 'data(height)',
          'background-opacity': '0',
          'background-image': 'data(image)',
          'background-fit': 'contain',
          'text-halign': 'center',
          'text-valign': 'bottom',
          'font-family': 'Comfortaa',
          'color': '#767676',
          // 'text-opacity': '0',
          'text-wrap': 'wrap',
          'text-background-shape': 'round-rectangle',
          'text-margin-y': 10,
          'text-background-color': 'white',
          "text-background-opacity": 1
        },
        css: {}
      },
      {
        // edge styling
        'selector': 'edge',
        style: {
          width: 2,
          'target-label': 'data(label)',
          'target-arrow-shape': 'triangle',
          'target-arrow-color': '#767676',
          'curve-style': 'taxi',
          'taxi-direction': 'horizontal',
          'taxi-turn': 15,
          'taxi-turn-min-distance': 5,
          'text-halign': 'center',
          'text-valign': 'bottom',
          'text-margin-y': 13,
          'target-text-margin-x': -25,
          'target-text-margin-y': -10,
          'text-background-color': 'white',
          "text-background-opacity": 1,
          'font-family': 'Comfortaa',
          'color': '#767676',
          'line-color': '#767676'
        }
      },
    ];
    if (!document.getElementById('cy')) return;
    this.cy = cytoscape({
      container: document.getElementById('cy'),
      style: style,
      minZoom: 0.1,
      maxZoom: 3,
    });
  }

  private connectNodes() {
    if (!this.project) return false;
    this.products = this.project.plantSpecifications.generatedProducts;
    let feedstockTarget: string = '';
    // Connect feedstocks to mixer or digester based on digestion type
    switch (this.project.plantSpecifications.selectedDigester) {
      case DigesterType.DRY_BATCH:
        feedstockTarget = 'digester';
        break;
      case DigesterType.WET_LAGOON:
      case DigesterType.DRY_CONT:
      case DigesterType.WET_MIXED:
        feedstockTarget = 'mixer';
        break;
    }
    // Connect feedstocks
    for (const feed of this.project.feedstocks) {
      if (!this.checkElements(['cy'])) return false;
      this.cy.add({
        data: {
          id: feed.name + '_digester_input',
          source: feed.name,
          target: feedstockTarget,
          label: '',
        }
      });
    }
    // Connect products
    for (const prod of this.project.plantSpecifications.generatedProducts) {

      let digesterConnected = false;
      const origin = this.getOriginFromProduct(prod);

      // Ignore multiple digesters after the first one is connected
      if (origin === 'digester' && digesterConnected) {
        continue;
      }

      if (origin === 'digester') {
        this.cy.add({
          data: {
            id: prod.type + 'To' + origin,
            label: '',
            source: 'digesterDigestateOutput',
            target: prod.type
          }
        });
      } else {
        // Get products generated by this unit
        const products = this.getProductsForNode(origin);
        if (products && products.length > 0) {
          if (!this.checkElements(['cy'])) return false;
          this.cy.add({
            data: {
              id: prod.type + 'To' + origin,
              label: '',
              source: prod.type + 'Output' + origin,
              target: prod.type
            },
            style: {
              'taxi-turn': (products.length === 1) ? '100%' : 15,
            }
          });
        }
      }

      if (origin === 'digester') {
        digesterConnected = true;
      }
    }
    return true;
  }

  /**
   * Create an array with empty items to center items in the array
   *
   * @param items feedstocks or products to center
   * @param maxLength the greater of the products or feedstocks length
   */
  private spaceOutArrayItems(items: Product[] | Feedstock[], maxLength: number) {
    const spacedOutItems: any[] = [];
    // Creates an array of max length filled with empty objects (undefined)
    spacedOutItems.length = maxLength;

    const offset = Math.floor(Math.abs(maxLength - items.length) / 2);
    for (let i = offset, s = items.length + offset; i < s; i++) {
      spacedOutItems[i] = items[i - offset];
    }

    return spacedOutItems;
  }

  private addFeedstocks() {
    if (!this.project) return false;
    // Sort feedstocks based on storage order
    const feeds = this.project.feedstocks;

    for (let i = 0; i < feeds.length; i++) {
      if (feeds[i] === undefined) {
        continue;
      }
      const name = feeds[i].name;
      const sprite = this.getImageFromFeedstockType(feeds[i]);
      const data: cytoscape.NodeDataDefinition = {
        id: name,
        type: 'rectangle',
        col: this.currentColumn,
        row: i,
        height: sprite.height,
        width: sprite.width,
        label: FormatUtil.supToUnicode(name + '\n' + this.getResultTextForFeed(feeds[i])),
        image: sprite.image,
      };
      if (!this.addResults) {
        data['navigateTo'] = '/project/feedstock/edit/' + i;
      }
      // try {
      if (!this.checkElements(['cy'])) return false;
      this.cy.add({ data: data });
      // } catch (e) {
      //   return;
      // }
      this.modelIdMap[name] = feeds[i];
    }
    return true;
  }

  private getImageFromFeedstockType(feedstock: Feedstock): Sprite {
    if (!feedstock.category) {
      const warning = 'PFD.FEEDSTOCK_ICON_WARNING.' + feedstock.name;
      if (!this.warnings[warning]) {
        this.toastrService.warning(this.translationService.get(
          'PFD.FEEDSTOCK_ICON_WARNING', { feedstockName: feedstock.name }));
        this.warnings[warning] = true;
      }
    }
    switch (feedstock.category) {
      case 'agriculture':
      case 'biological':
        if (
          feedstock.type.includes('animal waste')
          || feedstock.type.includes('manure')
          || feedstock.type.includes('slurry')
        ) {
          return Sprites.ANIMAL_PRODUCT;
        } else {
          return Sprites.AGRIWASTE;
        }
      case 'energy crops':
        return Sprites.ENERGY_CROPS;
      case 'municipal':
        if (feedstock.source.includes('sludge')) {
          return Sprites.MUNICIPAL_SEWAGE_SLUDGE;
        } else {
          return Sprites.MUNICIPAL_BIOWASTE;
        }
      case 'mixed feedstock':
      default:
        return Sprites.MUNICIPAL_BIOWASTE;
    }
  }

  private addDigesters() {
    if (!this.project) return false;

    let digesterSprite: Sprite;
    switch (this.project.plantSpecifications.selectedDigester) {
      case DigesterType.DRY_BATCH:
        digesterSprite = Sprites.DRY_BATCH_DIGESTER;
        break;
      case DigesterType.DRY_CONT:
        digesterSprite = Sprites.DRY_CONTINUOUS_DIGESTER;
        break;
      case DigesterType.WET_LAGOON:
        digesterSprite = Sprites.LAGOON_DIGESTER;
        break;
      case DigesterType.WET_MIXED:
      default:
        digesterSprite = Sprites.WET_CONTINUOUS_DIGESTER;
        break;
    }
    const digesterLabel = this.translationService.get('PFD.DIGESTER');

    if (!this.project.plantSpecifications.selectedDigester) {
      return false;
    }
    const data: cytoscape.NodeDataDefinition = {
      id: 'digester',
      type: 'rectangle',
      label: FormatUtil.supToUnicode(digesterLabel + '\n' + this.getDigesterVolume()),
      col: this.currentColumn,
      row: 'center',
      width: digesterSprite.width,
      height: digesterSprite.height,
      image: digesterSprite.image,
    };
    this.modelIdMap['digester'] = this.project.plantSpecifications;
    if (!this.addResults) {
      data.navigateTo = '/project/plant-specifications';
    }
    data.helperNodes = ['digesterBiogasOutput', 'digesterDigestateOutput'];
    if (!this.checkElements(['cy'])) return false;
    this.cy.add([
      {
        data: {
          id: 'digesterBiogasOutput',
          label: '',
          col: this.currentColumn,
          row: 'center',
          height: Sprites.EMPTY.height,
          width: Sprites.EMPTY.width,
          image: Sprites.EMPTY.image,
        }
      },
      {
        data: {
          id: 'digesterDigestateOutput',
          label: '',
          col: this.currentColumn,
          row: 'center',
          height: Sprites.EMPTY.height,
          width: Sprites.EMPTY.width,
          image: Sprites.EMPTY.image,
        }
      },
      { data: data },
    ]);
    this.currentColumn++;
    if (!this.checkElements(['cy'])) return false;
    this.cy.add([
      {
        data: {
          id: 'biogas_flow',
          label: FormatUtil.supToUnicode(this.translationService.get('PFD.BIOGAS') + '\n' + this.getBiogasAmount()),
          type: 'rectangle',
          col: this.currentColumn,
          width: Sprites.BIOGAS_FLOW.width,
          height: Sprites.BIOGAS_FLOW.height,
          image: Sprites.BIOGAS_FLOW.image
        }
      },
      {
        data: {
          id: 'digesterToBiogasFlow',
          label: '',
          source: 'digesterBiogasOutput',
          target: 'biogas_flow'
        }
      }
    ]);
    // Connect influent to digester
    if (this.project.plantSpecifications.selectedDigester !== DigesterType.DRY_BATCH) {
      if (!this.checkElements(['cy'])) return false;
      this.cy.add([
        {
          data: {
            id: 'influent_digester',
            label: '',
            source: 'influent',
            target: 'digester',
          }
        }
      ]);
    }
    // Add whole digestate product if dewatering is active
    if (this.project.plantSpecifications.selectedUnits.includes(PlantUnitType.DEWATERING)) {
      if (!this.checkElements(['cy'])) return false;
      this.cy.add([
        {
          data: {
            id: 'whole_digestate_flow',
            label: FormatUtil.supToUnicode(this.translationService.get('PFD.WHOLE_DIGESTATE') + '\n' + this.getWholeDigestateForDewatering()),
            type: 'rectangle',
            col: this.currentColumn,
            width: Sprites.WHOLE_DIGESTATE_FLOW.width,
            height: Sprites.WHOLE_DIGESTATE_FLOW.height,
            image: Sprites.WHOLE_DIGESTATE_FLOW.image
          }
        },
        {
          data: {
            id: 'digesterToWholeDigestateFlow',
            label: '',
            source: 'digesterDigestateOutput',
            target: 'whole_digestate_flow'
          }
        }
      ]);
    }
    return true;
  }

  private addProcessing() {
    if (!this.project) return false;

    this.recyclingAdded = false;
    this.collectProcessingNodes();
    const generatedProducts = this.getSortedProducts();
    // Start at the first row a product appears
    let currentRow = generatedProducts.findIndex(prod => prod !== undefined);
    let biogasProducts = 0;
    for (let i = 0; i < this.processingNodes.length; i++) {
      const node = this.processingNodes[i];
      // Don't add dewatering unit if dry batch
      if (node.type === PlantUnitType.DEWATERING && this.project.plantSpecifications.selectedDigester === DigesterType.DRY_BATCH) {
        continue;
      }
      const sprite = this.getImageFromProcessing(node.type);
      const data: LooseObject = {
        id: node.type,
        label: FormatUtil.supToUnicode(this.translationService.get(node.type) + '\n' + this.getProductOutput(node)),
        col: this.currentColumn,
        row: currentRow,
        image: sprite.image,
        height: sprite.height,
        width: sprite.width,
      };

      // Update the current row so that the next processing node is added below this node's products
      // const products = this.project.plantSpecifications.generatedProducts.filter(product => product.originComponents.includes(node.ref.uuid));
      const products = this.getProductsForNode(node.type);
      currentRow += (products.length > 0) ? products.length : 1;
      if (!this.addResults) {
        data['navigateTo'] = node.path;
      }
      // Add helper nodes to connect individual products to
      if (products.length > 0) {
        data['helperNodes'] = [];
      }
      for (let i = 0; i < products.length; i++) {
        if (!this.checkElements(['cy'])) return false;
        this.cy.add([
          {
            data: {
              id: products[i].type + 'Output' + node.type,
              label: '',
              col: this.currentColumn,
              row: currentRow - ((products.length > 0) ? products.length : 1),
              height: Sprites.EMPTY.height,
              width: Sprites.EMPTY.width,
              image: Sprites.EMPTY.image,
            }
          },
        ]);
        data['helperNodes'].push(products[i].type + 'Output' + node.type);
      }
      if (!this.checkElements(['cy'])) return false;
      this.cy.add({
        data: data
      });
      this.modelIdMap[node.type] = this.project.plantSpecifications;
      if (node.type === PlantUnitType.DEWATERING) {

        // Set the origin flow row when first encountered
        const flowElement = this.cy.getElementById('whole_digestate_flow');
        if (flowElement.data('row') === undefined) {
          flowElement.data('row', currentRow - products.length);
        }
        if (!this.checkElements(['cy'])) return false;
        this.cy.add({
          data: {
            id: 'digester_' + node.type,
            label: '',
            source: 'whole_digestate_flow',
            target: node.type,
          }
        });
      } else {
        biogasProducts += (products.length > 0) ? products.length : 1;
        const perc = Utils.formatNumber(this.project.plantSpecifications.getBiogasShareFromType(node.type), 2) + '%';
        if (!this.checkElements(['cy'])) return false;
        this.cy.add({
          data: {
            id: 'biogas' + node.type,
            label: perc,
            source: 'biogas_flow',
            target: node.type,
          }
        });
      }

      // add recycle if needed
      if (this.project.plantSpecifications.hasDewatering() && node.type === PlantUnitType.DEWATERING) {
        if (!this.recyclingAdded) {
          if (!this.checkElements(['cy'])) return false;
          this.cy.add([{
            data: {
              id: 'recycling',
              label: FormatUtil.supToUnicode(this.translationService.get('PFD.RECYCLE_STREAM') + '\n' + this.getRecycling()),
              type: 'rectangle',
              col: this.currentColumn - 1,
              row: currentRow - products.length + 1,  // Add the recycle stream immediately below the dewatering unit
              height: Sprites.RECYCLE_STREAM_FLOW.height,
              width: Sprites.RECYCLE_STREAM_FLOW.width,
              image: Sprites.RECYCLE_STREAM_FLOW.image
            }
          }, {
            data: {
              id: 'recyclingToMixer',
              label: '',
              source: 'recycling',
              target: 'mixer'
            },
            style: {
              'taxi-turn': '100%',
            }
          }]);
          if (products.length === 1) {
            currentRow++;  // Increment the current row to account for the recycle stream
          }
          this.recyclingAdded = true;
        }
        // hard coded source. Checks were performed to make sure there is dewatering and recycling
        if (!this.checkElements(['cy'])) return false;
        this.cy.add({
          data: {
            id: node.type + 'ToRecycling',
            label: '',
            source: 'thin digestate biofertilizerOutput' + node.type,
            target: 'recycling'
          }
        });
      }
    }
    // Set the origin flow row in the middle of its products
    const flowElement = this.cy.getElementById('biogas_flow');
    if (flowElement.data('row') === undefined) {
      flowElement.data('row', Math.floor(biogasProducts / 2));
    }
    return true;
  }

  private getImageFromProcessing(type: string): Sprite {
    switch (type) {
      case PlantUnitType.BOILER:
        return Sprites.BOILER;
      case PlantUnitType.DEWATERING:
        return Sprites.DEWATERER;
      case PlantUnitType.UPGRADER:
        return Sprites.UPGRADER;
      case PlantUnitType.GENERATOR:
        return Sprites.GENERATOR;
      case PlantUnitType.CHP:
      default:
        return Sprites.CHP;
    }
  }

  saveGraphic() {
    var image = new Image();
    var canvas: HTMLCanvasElement = <HTMLCanvasElement>document.getElementById("canvas");
    var context = canvas.getContext("2d");

    image.onload = () => {
      if (!context) return;
      canvas.width = image.width;
      canvas.height = image.height;

      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);

      context.drawImage(image, 0, 0, image.width, image.height);

      const fileName = 'flow';
      const link = document.createElement('a');
      link.style.backgroundColor = 'white';
      link.download = fileName + '.png';
      canvas.toBlob(blob => {
        blob
        if (!blob) return;
        link.href = URL.createObjectURL(blob);
        link.click();
      }, "image/png", 1);

    };

    var png64 = this.cy.png({ full: true });
    image.src = png64;
  }

  public promiseSaveImageAs(fileName?: string, purpose?: string): Promise<string> {
    if (!fileName) {
      fileName = Date.now().toString() + '.png';
    }
    const nameAndFormat = fileName.split('.');
    const nameOnly = nameAndFormat[0];
    return new Promise((fulfill, reject) => {

      var image = new Image();
      var canvas: HTMLCanvasElement = <HTMLCanvasElement>document.getElementById("canvas");
      var context = canvas.getContext("2d");

      image.onload = () => {
        if (!context) return;
        canvas.width = image.width;
        canvas.height = image.height;

        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, canvas.width, canvas.height);

        context.drawImage(image, 0, 0, image.width, image.height);

        canvas.toBlob(blob => {
          const uploadDoneNotifier = new EventEmitter();
          this.commService.storeBlobAsFileSilently(blob, nameOnly, purpose)
            .pipe(takeUntil(this.onDestroyNotifier))
            .pipe(takeUntil(uploadDoneNotifier))
            .subscribe(
              absolutePath => {
                if (absolutePath && typeof absolutePath === 'string') {
                  uploadDoneNotifier.emit();
                  fulfill(absolutePath);
                }
              }
            );
        }, "image/png", 1);

      };

      var png64 = this.cy.png({ full: true });
      image.src = png64;
    });
  }


  /**
   * Returns the list of products sorted by the order of processing nodes
   */
  private getSortedProducts(): Product[] {
    if (!this.project) return [];

    this.products = Utils.deepCopy(this.project.plantSpecifications.generatedProducts);
    let generatedProducts: Product[] = Utils.deepCopy(this.products);
    generatedProducts = generatedProducts.sort((a: Product, b: Product) => {
      const origA = this.getOriginFromProduct(a);
      const origB = this.getOriginFromProduct(b);

      let indexA = 0;
      let indexB = 0;
      const nodes = Utils.deepCopy(this.processingNodes);
      nodes.push({ type: 'digester', path: null });

      for (let i = 0; i < nodes.length; i++) {
        if (nodes[i].type === origA) {
          indexA = i;
        }

        if (nodes[i].type === origB) {
          indexB = i;
        }
      }
      if (indexA === indexB) {
        return 0;
      } else if (indexA > indexB) {
        return 1;
      } else {
        return -1;
      }
    });
    // Add spacing if more feedstocks than products
    if (this.project.feedstocks.length > this.products.length) {
      const maxLength = Math.max(this.products.length, this.project.feedstocks.length);
      return this.spaceOutArrayItems(generatedProducts, maxLength);
    }
    return generatedProducts;
  }

  private addProducts() {
    const generatedProducts = this.getSortedProducts();
    for (let i = 0; i < generatedProducts.length; i++) {
      if (generatedProducts[i] === undefined) {
        continue;
      }
      const prod = new Product(generatedProducts[i]);
      const sprite = this.getImageForProduct(prod);
      // add product
      const data: cytoscape.NodeDataDefinition = {
        id: prod.type,
        label: FormatUtil.supToUnicode(prod.name + '\n' + this.getProductResult(prod.type)),
        col: this.currentColumn,
        row: i,
        width: sprite.width,
        height: sprite.height,
        image: sprite.image,
      };
      if (!this.addResults) {
        data.navigateTo = '/project/plant-specifications';
      }
      if (!this.checkElements(['cy'])) return false;
      this.cy.add({
        data: data
      });
      // remember the model used for the node
      this.modelIdMap[prod.type] = prod;
    }
    return true;
  }

  private getImageForProduct(prod: Product): Sprite {
    switch (prod.type) {
      case ProductType.Electric:
        return Sprites.POWER;
      case ProductType.Biometh:
        return Sprites.RNG;
      case ProductType.Heat:
        return Sprites.HEAT;
      case ProductType.SolidDigestate:
        return Sprites.FERTILIZER;
      case ProductType.WholeDigestate:
        const digesterType = this.project?.plantSpecifications.selectedDigester ?? '';
        if (digesterType === DigesterType.DRY_BATCH || digesterType === DigesterType.DRY_CONT) {
          return Sprites.FERTILIZER;
        } else {
          return Sprites.LIQUID_FERTILIZER;
        }
      case ProductType.ThinDigestate:
        return Sprites.LIQUID_FERTILIZER;
      default:
        return Sprites.LIQUID_FERTILIZER;
    }
  }

  private getResultTextForFeed(feedstock: Feedstock) {
    if (!this.addResults) {
      return '';
    }
    const unit = this.getUnitForSelectedUnitType(UnitTranslationId.TONNE);
    const imperialSelected = this.techUnitType === 'imperial';
    let mass = this.feedstockService.getNormalizedMassPerYearForUnitType(feedstock, imperialSelected);
    mass = this.convertValToPeriod(mass);
    return Utils.formatNumber(mass, this.precision) + ' ' + Unit.parseUnitDisplayName(this.translationService.get(unit));
  }

  private getDigesterVolume() {
    if (!this.addResults || !this.results) {
      return '';
    }

    const resultRow = this.results.technical.general.oapp.dwv;
    if (!resultRow) {
      return '';
    }
    const convertedRow = this.performRowConversion(resultRow);
    const unit = this.getUnitForSelectedUnitType(UnitTranslationId.M3);
    return this.translationService.get('PFD.WORKING_VOLUME') + ' ' +
      Utils.formatNumber(Results.getValueFromTechResult(convertedRow), this.precision) + ' ' +
      Unit.parseUnitDisplayName(this.translationService.get(unit));
  }

  private getBiogasAmount() {
    if (!this.addResults || !this.results) {
      return '';
    }
    const row = this.results.technical.general.dof.bp;
    if (!row) {
      return '';
    }
    const convertedRow = this.performRowConversion(row);
    const unit = this.getUnitForSelectedUnitType(UnitTranslationId.NM3);
    return Utils.formatNumber(Results.getValueFromTechResult(convertedRow), this.precision) + ' ' +
      Unit.parseUnitDisplayName(this.translationService.get(unit));
  }

  private getProductOutput(node: ProcessNode) {
    if (!this.addResults) {
      return '';
    }
    return '';
  }

  private getInfluent() {
    if (!this.addResults || !this.results) {
      return '';
    }
    const row = this.results.technical.general.dif.imfr;
    if (!row) {
      return '';
    }
    const convertedRow = this.performRowConversion(row);
    const unit = this.getUnitForSelectedUnitType(UnitTranslationId.TONNE);
    return Utils.formatNumber(Results.getValueFromTechResult(convertedRow), this.precision) + ' ' +
      Unit.parseUnitDisplayName(this.translationService.get(unit));
  }

  private getFreshWaterInput() {
    if (!this.addResults || !this.results) {
      return '';
    }
    const row = this.results.technical.general.dif.rfw;
    if (!row) {
      return '';
    }
    const convertedRow = this.performRowConversion(row);
    const unit = this.getUnitForSelectedUnitType(UnitTranslationId.M3);
    return Utils.formatNumber(Results.getValueFromTechResult(convertedRow), this.precision) + ' ' +
      Unit.parseUnitDisplayName(this.translationService.get(unit));
  }

  private getRecycling() {
    if (!this.addResults || !this.results) {
      return '';
    }
    const row = this.results.technical.general.dif.rd;
    if (!row) {
      return '';
    }
    const convertedRow = this.performRowConversion(row);
    const unit = this.getUnitForSelectedUnitType(UnitTranslationId.M3);
    return Utils.formatNumber(Results.getValueFromTechResult(convertedRow), this.precision) + ' ' +
      Unit.parseUnitDisplayName(this.translationService.get(unit));
  }

  private getProductResult(type: string) {
    if (!this.addResults || !this.results) {
      return '';
    }

    if (!this.results.technical) {
      return '';
    }
    const techResults = this.results.technical;

    let key: string;
    let unit: string;
    const result: string[] = [];
    switch (type) {
      case ProductType.Electric:
        key = 'PRODUCTS.ELECTRICITY';
        unit = this.getUnitForSelectedUnitType(UnitTranslationId.KWH);
        break;
      case ProductType.Heat:
        key = 'PRODUCTS.THERMAL';
        unit = this.getUnitForSelectedUnitType(UnitTranslationId.KWH);
        break;
      case ProductType.Biometh:
        key = 'RESULTS.DATA.TECHNICAL.RNGP';
        if (!techResults.ppd || !techResults.ppd.sp || !techResults.ppd.sp[key]) {
          return '';
        }
        const rngRow = techResults.ppd.sp[key];
        const rngVal = Results.getValueFromTechResult(this.performRowConversion(rngRow));
        if (rngVal === null) return '';
        unit = this.getUnitForSelectedUnitType(UnitTranslationId.NM3);
        return [
          this.translationService.get('PFD.SOLD') + ': ' + Utils.formatNumber(rngVal, this.precision) + ' ' +
          Unit.parseUnitDisplayName(this.translationService.get(unit))
        ];
      case ProductType.WholeDigestate:
        unit = this.getUnitForSelectedUnitType(UnitTranslationId.M3);
        key = 'PRODUCTS.WDBF';
        break;
      case ProductType.ThinDigestate:
        key = 'PRODUCTS.TDBF';
        unit = this.getUnitForSelectedUnitType(UnitTranslationId.M3);
        break;
      case ProductType.SolidDigestate:
        key = 'PRODUCTS.SDBF';
        unit = this.getUnitForSelectedUnitType(UnitTranslationId.TONNE);
        break;
      default:
        return '';
    }
    const producedVal = this.getProdDemandResult(techResults, 'gp', key);
    const soldVal = this.getProdDemandResult(techResults, 'sp', key);
    const internalVal = this.getProdDemandResult(techResults, 'iup', key);
    if (producedVal !== null) {
      result.push(this.translationService.get('PFD.PRODUCED') + ': ' + this.getProdDemandResult(techResults, 'gp', key) + ' ' +
        Unit.parseUnitDisplayName(this.translationService.get(unit)),
        '\n');
    }
    if (soldVal !== null) {
      result.push(this.translationService.get('PFD.SOLD') + ': ' +
        this.getProdDemandResult(techResults, 'sp', key) + ' ' +
        Unit.parseUnitDisplayName(this.translationService.get(unit)),
        '\n');
    }
    if (internalVal !== null) {
      result.push(this.translationService.get('PFD.INTERNAL_USE') + ': ' + this.getProdDemandResult(techResults, 'iup', key) + ' ' +
        Unit.parseUnitDisplayName(this.translationService.get(unit)));
    }
    const s = result.join('');
    if (!s) {
      return '';
    }
    return s;
  }

  getWholeDigestateForDewatering() {
    if (!this.addResults || !this.results || !this.results.technical) {
      return '';
    }
    const techResults = this.results.technical;
    const unit = this.getUnitForSelectedUnitType(UnitTranslationId.M3);
    const wholeRow = techResults.general.dof.dfr;
    const wholeVal = Results.getValueFromTechResult(this.performRowConversion(wholeRow));

    if (!wholeRow || !wholeVal) {
      return '';
    }
    return Utils.formatNumber(wholeVal, this.precision) + ' ' + Unit.parseUnitDisplayName(this.translationService.get(unit));
  }

  private getProdDemandResult(techResults: TechnicalResults, type: keyof PlantProductDemand, product: string): string | null {
    if (!techResults || !techResults.ppd || !techResults.ppd[type]) {
      return null;
    }
    const row = (techResults.ppd[type] as LooseObject)[product];
    if (!row) return null;

    return Utils.formatNumber(
      Results.getValueFromTechResult(
        this.performRowConversion(row)
      ),
      this.precision
    );
  }

  private createPopperWindowForNode(id: string) {
    const myNode = this.cy.getElementById(id);
    this.currentPopperPopup = <PopperInstance>myNode['popper']({
      content: () => {
        if (!this.popperDiv) return new HTMLDivElement();
        this.popperDiv.nativeElement.innerHTML = this.getSummaryHTML(id);
        if (this.popperDiv.nativeElement.innerHTML === '') {
          this.popperDiv.nativeElement.style.visibility = 'hidden';
        }
        return this.popperDiv.nativeElement;
      },
      popper: {
        placement: 'right',
      }
    });
    this.cy.on('pan zoom resize', this.updatePopper);
    myNode.on('position', this.updatePopper);
  }

  private getSummaryHTML(id: string) {
    const myModel = this.modelIdMap[id];
    return this.summaryGenerator.getPopupInputSummary(myModel, id);
  }

  ngOnDestroy(): void {
    this.cy.removeAllListeners();
    this.cy.destroy()
    setTimeout(() => {
      this.onDestroyNotifier.emit(true);
    });
    if (this.popperDiv) {
      this.popperDiv.nativeElement.style.visibility = 'hidden';
      this.popperDiv.nativeElement.innerHTML = '';
    }
  }

  private getProductsForNode(type: string): Product[] {
    if (!this.project) {
      return [];
    }

    const prods = Utils.deepCopy(this.project.plantSpecifications.generatedProducts);
    switch (type) {
      case PlantUnitType.DEWATERING:
        return prods.filter(prod =>
          prod.type === ProductType.ThinDigestate || prod.type === ProductType.SolidDigestate);
      case PlantUnitType.BOILER:
        return prods.filter(prod => prod.type === ProductType.Heat);
      case PlantUnitType.CHP:
        return prods.filter(prod =>
          prod.type === ProductType.Electric || prod.type === ProductType.Heat);
      case PlantUnitType.GENERATOR:
        return prods.filter(prod => prod.type === ProductType.Electric);
      case PlantUnitType.UPGRADER:
        return prods.filter(prod => prod.type === ProductType.Biometh);
      default:
        console.log('Unknown node type');
        return [];
    }
  }


  private getUnitTypeFromString(unit: string): PlantUnitType | null {
    return Object.values(PlantUnitType).includes(unit as PlantUnitType)
      ? unit as PlantUnitType
      : null;
  }


  private getOriginFromProduct(prod: Product) {
    if (!this.project) return '';

    switch (prod.type) {
      case ProductType.ThinDigestate:
      case ProductType.SolidDigestate:
        if (this.project && this.project.plantSpecifications && this.project.plantSpecifications.hasDewatering()) {
          return PlantUnitType.DEWATERING;
        } else {
          return 'digester';
        }
      case ProductType.Biometh:
        return PlantUnitType.UPGRADER;
      case ProductType.Electric:
        if (this.project.plantSpecifications.selectedUnits.includes(PlantUnitType.GENERATOR)) {
          return PlantUnitType.GENERATOR;
        } else {
          return PlantUnitType.CHP;
        }
      case ProductType.Heat:
        if (this.project.plantSpecifications.selectedUnits.includes(PlantUnitType.BOILER)) {
          return PlantUnitType.BOILER;
        }
        return PlantUnitType.CHP;
      case ProductType.WholeDigestate:
        return 'digester';
    }
  }

  private performRowConversion(row: ResultRow) {
    if (row == null) {
      return row;
    }
    const convertedRow = this.convertResultRowToTargetUnit(row.deepCopy());
    return this.convertToPeriod(convertedRow);
  }


  /**
   * Converts result data based on the select period
   * @param resultRow
   */
  convertToPeriod(row: ResultRow): ResultRow {
    if (row.label === 'RESULTS.DATA.TECHNICAL.OLR') {
      return row;
    }

    const ignoredRows = [
      'RESULTS.DATA.TECHNICAL.DWV', // Digester working volume
    ];
    if (ignoredRows.includes(row.label ?? '')) {
      return row;
    }

    // Values from results are per day values
    if (this.periodType === 'month') {
      row.values[0] = row.values[0] * 30;
    } else if (this.periodType === 'year') {
      row.values[0] = row.values[0] * 365;
    }
    return row;
  }

  /**
   * converts a single value to period assuming it is in years
   */
  convertValToPeriod(value: number) {
    if (this.periodType === 'month') {
      return value / 12;
    } else if (this.periodType === 'day') {
      return value / 365;
    }
    return value;
  }

  /**
   * Converts result data based on the selected measurement system
   * @param row technical result data row
   */
  convertResultRowToTargetUnit(row: ResultRow): any {
    let imperialUnit: Unit | null = null;
    let metricUnit: Unit | null = null;
    if (row && row.unit) {
      metricUnit = this.unitService.getUnitById(row.unit);
      if (this.techUnitType === 'imperial') {
        imperialUnit = this.getImperialCounterpart(row.unit);
      }
      if (imperialUnit && imperialUnit.translationId) {
        row.unit = Unit.parseUnitDisplayName(this.translationService.get(imperialUnit.translationId));
      } else if (metricUnit && metricUnit.translationId) {
        imperialUnit = null;
        row.unit = Unit.parseUnitDisplayName(this.translationService.get(metricUnit.translationId));
      } else {
        row.unit = 'n/a';
      }
    }
    if (row && row.values) {
      if (metricUnit && imperialUnit) {
        row.values[0] = metricUnit.convertToUnit(row.values[0], imperialUnit);
      } else {
        row.values = row.values;
      }
    }
    return row;
  }

  /**
   * Returns the metric unit counterpart in the imperial system
   * @param unitId unit id
   */
  private getImperialCounterpart(unitId: string): Unit | null {
    let imperialUnitId = '';
    switch (unitId) {
      case '000000000000000000003601': // kg VS/m3/day
        imperialUnitId = '000000000000000000003604'; // lb VS/ft3/day
        break;
      case '000000000000000000001203': // t (tonne)
        imperialUnitId = '000000000000000000001204'; // t (short ton)
        break;
      case '000000000000000000004901': // ton/day
        imperialUnitId = '000000000000000000004902'; // short ton/day
        break;
      case '000000000000000000004905': // ton/year
        imperialUnitId = '000000000000000000004906'; // short ton/year
        break;
      case '000000000000000000001802': // m3
        imperialUnitId = '000000000000000000001803'; // ft3
        break;
      case '000000000000000000003803': // m3/day
        imperialUnitId = '000000000000000000003806'; // ft3/day
        break;
      case '000000000000000000003805': // Nm3/day
        imperialUnitId = '000000000000000000003804'; // scf/day
        break;
      case '000000000000000000005201': // kg CO2eq/day
        imperialUnitId = '000000000000000000005203'; // lbs CO2eq/day
        break;
      case '000000000000000000003708': // kWh/day
      case '000000000000000000000401': // %
        return null;
      default:
        imperialUnitId = unitId;
    }
    return this.unitService.getUnitById(imperialUnitId);
  }

  private getUnitForSelectedUnitType(metricUnit: UnitTranslationId) {
    if (this.techUnitType !== 'imperial') return metricUnit;
    switch (metricUnit) {
      case UnitTranslationId.TONNE:
        return UnitTranslationId.SHORT_TON;
      case UnitTranslationId.KWH:
        return metricUnit;
      case UnitTranslationId.M3:
        return UnitTranslationId.FT3;
      case UnitTranslationId.NM3:
        return UnitTranslationId.SCF;
      default:
        return '';
    }
  }

  onResultSetDefaultChange(): void {
    if (this.resultSetConf?.dropdownConfigEnabled && Utils.isMap(this.resultSetConf.dropdownDefaults)) {
      this.resultSetConf.dropdownDefaults.set(ResultSetDropdownId.PFD_PERIOD, this.periodType);
      this.resultSetConf.dropdownDefaults.set(ResultSetDropdownId.PFD_UNIT_TYPE, this.techUnitType);
    }
    this.resultSetConfSaveNotifier.emit();
  }

  processResultSetDefaults(): void {
    if (this.resultSetConf?.dropdownConfigEnabled && Utils.isMap(this.resultSetConf.dropdownDefaults)) {
      const defaults = this.resultSetConf.dropdownDefaults;
      if (this.resultSetConf.dropdownDefaults.has(ResultSetDropdownId.PFD_PERIOD)) {
        this.periodType = defaults.get(ResultSetDropdownId.PFD_PERIOD);
      }
      if (this.resultSetConf.dropdownDefaults.has(ResultSetDropdownId.PFD_UNIT_TYPE)) {
        this.techUnitType = defaults.get(ResultSetDropdownId.PFD_UNIT_TYPE);
      }
    }
  }

  private checkElements(elementIDs: string[]) {
    for (const id of elementIDs) {
      if (!document.getElementById(id)) return false;
    }
    return true;
  }
}

class ProcessNode {
  constructor(
    public type: PlantUnitType | 'digester',
    public path: string | null,
  ) {
  }
}

enum UnitTranslationId {
  TONNE = 'DATABASE.UNITS.MASS.TON',
  SHORT_TON = 'DATABASE.UNITS.MASS.SHORT_TON',
  KWH = 'DATABASE.UNITS.THERMAL_QUANTITY.KWH',
  M3 = 'DATABASE.UNITS.VOLUME_PERCENT.M3',
  FT3 = 'DATABASE.UNITS.VOLUME_PERCENT.FT3',
  NM3 = 'DATABASE.UNITS.VOLUME_GAS.M3',
  SCF = 'DATABASE.UNITS.VOLUME_GAS.SCF'
}
